import { useMemo } from 'react';

import * as tracking from './tracking';
import { useApplicationContext } from '../applicationContext';
import { useDeviceType } from '@cian/ui-kit';

import {
  IOfferForTrackOnClickButton,
  IOffersForShowMultiOffers,
  IOffersForShowOffers,
  TRedirectForAnalytics,
  TTargetElementForAnalytics,
} from './types';

import { EFillApplicationStickyButtonType } from '../../containers/stickyButtons/FillApplicationButton/types';
import { replaceUndefinedWithNull } from '../common/replaceUndefinedWithNull';
import { ICalculatorMcfValues } from '../../types/features';

export const useTracking = () => {
  const deviceType = useDeviceType();
  const {
    custom: { userId },
  } = useApplicationContext();

  return useMemo(() => {
    const pageProps = { deviceType, userId };

    const trackClickOnTargetButton = () => {
      tracking.trackClickOnTargetButton(pageProps);
    };

    const trackEditFormValues = (userInput: ICalculatorMcfValues) => {
      const formData = replaceUndefinedWithNull(userInput);

      tracking.trackEditFormValues({ ...pageProps, userInput: formData });
    };

    const trackClickOnMoreFilters = () => {
      tracking.trackClickOnMoreFilters(pageProps);
    };

    const trackClickOnCard = (offer: IOfferForTrackOnClickButton | null) => {
      tracking.trackClickOnCard({ ...pageProps, offer });
    };

    const trackClickOnMasterButton = (type: EFillApplicationStickyButtonType) => {
      tracking.trackClickOnMasterButton({ ...pageProps, type });
    };

    const trackClickOnModalFillApplicationButton = (offer: IOfferForTrackOnClickButton | null) => {
      tracking.trackClickOnModalFillApplicationButton({ ...pageProps, offer });
    };

    const trackShowOffers = (offers: IOffersForShowOffers) => {
      tracking.trackShowOffers({ ...pageProps, offers });
    };

    const trackShowMultiOffers = (bank: string, offers: IOffersForShowMultiOffers) => {
      tracking.trackShowMultiOffer({ ...pageProps, offers, bank });
    };

    const trackClickOpenMultiOffer = () => {
      tracking.trackClickOpenMultiOffer(pageProps);
    };

    const trackMobileShowButtonAmountOffers = (amount: number | null) => {
      tracking.trackMobileShowButtonAmountOffers({ ...pageProps, amount });
    };

    const trackScrollToCalculatorButton = () => {
      tracking.trackScrollToCalculatorButton(pageProps);
    };

    const trackScrollToOffersButton = () => {
      tracking.trackScrollToOffersButton(pageProps);
    };

    const trackOpenPhoneAuthModal = () => {
      tracking.trackOpenPhoneAuthModal(pageProps);
    };

    const trackOpenSmsAuthModal = () => {
      tracking.trackOpenSmsAuthModal(pageProps);
    };

    const trackClickOnRepeatSms = () => {
      tracking.trackClickOnRepeatSms(pageProps);
    };

    const trackClickConfirmPhone = () => {
      tracking.trackClickConfirmPhone(pageProps);
    };

    const trackAgreeToCommercial = () => {
      tracking.trackAgreeToCommercial(pageProps);
    };

    const trackClickConfirmSms = () => {
      tracking.trackClickConfirmSms(pageProps);
    };

    const trackRedirect = (
      type: TRedirectForAnalytics | null,
      object: TTargetElementForAnalytics | null,
      bank?: string | null,
    ) => tracking.trackRedirect({ ...pageProps, bank: bank || null, type, object });

    const trackAuthorizationSuccess = () => tracking.trackAuthorizationSuccess(pageProps);

    const trackScrollToLeadForm = () => {
      tracking.trackScrollToLeadForm(pageProps);
    };

    const trackInputLeadForm = (value: string) => {
      tracking.trackInputLeadForm({ ...pageProps, value });
    };

    const trackCheckboxAdsLeadForm = (checkboxType: 'On' | 'Off') => {
      tracking.trackCheckboxAdsLeadForm({ ...pageProps, checkboxType });
    };

    const trackContinueLeadForm = () => {
      tracking.trackContinueLeadForm(pageProps);
    };

    const trackSuccessLeadForm = () => {
      tracking.trackSuccessLeadForm(pageProps);
    };

    return {
      trackClickOnTargetButton,
      trackEditFormValues,
      trackClickOnMoreFilters,
      trackClickOnCard,
      trackClickOnMasterButton,
      trackClickOnModalFillApplicationButton,
      trackShowOffers,
      trackShowMultiOffers,
      trackClickOpenMultiOffer,
      trackRedirect,
      trackAuthorizationSuccess,
      trackOpenPhoneAuthModal,
      trackOpenSmsAuthModal,
      trackClickOnRepeatSms,
      trackClickConfirmPhone,
      trackAgreeToCommercial,
      trackClickConfirmSms,
      trackMobileShowButtonAmountOffers,
      trackScrollToCalculatorButton,
      trackScrollToOffersButton,
      trackScrollToLeadForm,
      trackInputLeadForm,
      trackCheckboxAdsLeadForm,
      trackContinueLeadForm,
      trackSuccessLeadForm,
    };
  }, [deviceType, userId]);
};
